import React, { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '../../../components/ui/card';
import { Input } from '../../../components/ui/input';
import { Skeleton } from '../../../components/ui/skeleton';
import { Badge } from '../../../components/ui/badge';
import { IconTable, IconAlertTriangle, IconSearch, IconArrowUp, IconArrowDown } from '@tabler/icons-react';
import coverageVerificationAnalyticService from '../../../services/coverage-verification-analytics.service';

const PlanDrugCombosTable = ({ organization, dateRange, refreshKey }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'check_count', direction: 'desc' });

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await coverageVerificationAnalyticService.getMostPopularPlanDrugCombos({
          organization,
          dateRange,
          limit: 50,
        });
        setData(result.combos || []);
      } catch (err) {
        console.error('Failed to fetch plan-drug combos:', err);
        setError('Failed to load plan-drug combinations data');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organization, dateRange, refreshKey]);

  const handleSort = (key) => {
    setSortConfig({
      key,
      direction: sortConfig.key === key && sortConfig.direction === 'desc' ? 'asc' : 'desc',
    });
  };

  const filteredAndSortedData = React.useMemo(() => {
    let filtered = data.filter((item) =>
      Object.values(item).some((val) =>
        String(val).toLowerCase().includes(searchTerm.toLowerCase())
      )
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        const aVal = a[sortConfig.key];
        const bVal = b[sortConfig.key];
        if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }

    return filtered;
  }, [data, searchTerm, sortConfig]);

  const getCoverageColor = (rate) => {
    if (rate >= 80) return 'default';
    if (rate >= 50) return 'warning';
    return 'destructive';
  };

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) return null;
    return sortConfig.direction === 'desc' ? 
      <IconArrowDown size={14} className="inline ml-1" /> : 
      <IconArrowUp size={14} className="inline ml-1" />;
  };

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <CardHeader>
          <CardTitle className="text-base font-bold">Most Popular Plan-Drug Combinations</CardTitle>
          <CardDescription className="text-sm">
            Most frequently checked plan and drug pairings
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Skeleton className="w-full h-96" />
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-red-600">
            <IconAlertTriangle size={20} />
            {error}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="shadow-sm border-0">
      <CardHeader>
        <div className="flex items-center gap-2">
          <IconTable size={20} />
          <div className="flex-1">
            <CardTitle className="text-base font-bold">Most Popular Plan-Drug Combinations</CardTitle>
            <CardDescription className="text-sm">
              Most frequently checked plan and drug pairings
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {/* Search */}
        <div className="mb-4">
          <div className="relative max-w-md">
            <IconSearch size={18} className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by plan or drug name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 h-10 bg-muted/30 border-muted focus:bg-background"
            />
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Showing {filteredAndSortedData.length} of {data.length} combinations
          </p>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th 
                  className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => handleSort('plan_name')}
                >
                  Plan Name <SortIcon columnKey="plan_name" />
                </th>
                <th 
                  className="text-left py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => handleSort('drug_name')}
                >
                  Drug Name <SortIcon columnKey="drug_name" />
                </th>
                <th 
                  className="text-center py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => handleSort('check_count')}
                >
                  Checks <SortIcon columnKey="check_count" />
                </th>
                <th className="text-center py-3 px-4 text-sm font-semibold text-foreground">
                  Success / Fail
                </th>
                <th 
                  className="text-center py-3 px-4 text-sm font-semibold text-foreground cursor-pointer hover:bg-muted/70 transition-colors"
                  onClick={() => handleSort('coverage_rate')}
                >
                  Coverage Rate <SortIcon columnKey="coverage_rate" />
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedData.length === 0 ? (
                <tr>
                  <td colSpan={5} className="text-center py-8 text-muted-foreground">
                    {searchTerm ? 'No results match your search' : 'No data available'}
                  </td>
                </tr>
              ) : (
                filteredAndSortedData.map((item, index) => (
                  <tr key={index} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                    <td className="py-3 px-4">
                      <span className="font-medium text-sm">{item.plan_name}</span>
                    </td>
                    <td className="py-3 px-4">
                      <span className="font-medium text-sm">{item.drug_name}</span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant="outline" className="font-semibold">{item.check_count}</Badge>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <div className="flex gap-1 justify-center">
                        <Badge variant="default" className="bg-green-500 hover:bg-green-600 text-white text-xs">
                          ✓ {item.successful_checks}
                        </Badge>
                        {item.failed_checks > 0 && (
                          <Badge variant="destructive" className="text-xs">
                            ✗ {item.failed_checks}
                          </Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <Badge variant={getCoverageColor(item.coverage_rate)} className="min-w-[60px]">
                        {item.coverage_rate.toFixed(1)}%
                      </Badge>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default PlanDrugCombosTable;
